import type { StepKind } from '@segmently/schema-models/v2';

/**
 * Composite identity of a theme template: a StepKind plus an optional
 * user-defined `subtype`. Multiple templates of the same StepKind coexist in a
 * single theme, distinguished ONLY by subtype, so every "the template for this
 * screen" lookup must key on this composite — never on screenType alone.
 *
 * `undefined`, `''`, and whitespace-only all collapse to the same "no-subtype"
 * bucket, so legacy themes (no subtypes anywhere) resolve exactly as before:
 * an undefined-subtype screen matches an undefined-subtype template.
 *
 * Subtype is unique per StepKind (enforced at authoring time), so this key is a
 * unique primary key for a template → resolution is a deterministic lookup,
 * not a first-of-kind guess.
 */
export function kindSubtypeKey(kind: StepKind, subtype?: string): string {
  return `${kind}::${(subtype ?? '').trim()}`;
}

/**
 * UI/selection key for a template tile. Identical to the bare StepKind when the
 * template has no subtype — so legacy themes keep their exact selection keys,
 * testids (`apply-theme-tile-toggle-{kind}`), and labels — and becomes
 * `kind::subtype` only for authored variants. Use this for apply-selection sets
 * and tile testids; use `kindSubtypeKey` for routing/equality.
 */
export function templateTileKey(kind: StepKind | string, subtype?: string): string {
  const trimmed = (subtype ?? '').trim();
  return trimmed ? `${kind}::${trimmed}` : kind;
}
